import * as THREE from 'three';
import { normalizeDeg } from './GeoMath';

/**
 * DeviceOrientationEvent のラッパー(計画書§7.2)。
 * - iOS: requestPermission()(ユーザージェスチャー内必須) + webkitCompassHeading(真北基準)
 * - Android: deviceorientationabsolute を優先(磁北基準alpha)
 *
 * デバイス姿勢→カメラクォータニオン変換は旧three.js DeviceOrientationControls と同じ数学。
 */

interface DeviceOrientationEventiOS extends DeviceOrientationEvent {
  webkitCompassHeading?: number;
  webkitCompassAccuracy?: number;
}

type DeviceOrientationEventStatic = typeof DeviceOrientationEvent & {
  requestPermission?: () => Promise<'granted' | 'denied'>;
};

const DEG2RAD = Math.PI / 180;

const _zee = new THREE.Vector3(0, 0, 1);
const _euler = new THREE.Euler();
const _q0 = new THREE.Quaternion();
// カメラをデバイス背面向きにする(-PI/2 x軸回転)
const _q1 = new THREE.Quaternion(-Math.sqrt(0.5), 0, 0, Math.sqrt(0.5));

function quaternionFromDeviceOrientation(
  out: THREE.Quaternion,
  alphaRad: number,
  betaRad: number,
  gammaRad: number,
  screenOrientRad: number,
): void {
  _euler.set(betaRad, alphaRad, -gammaRad, 'YXZ'); // ZXY(デバイス) → 'YXZ'(three)
  out.setFromEuler(_euler);
  out.multiply(_q1);
  out.multiply(_q0.setFromAxisAngle(_zee, -screenOrientRad));
}

export class OrientationSource {
  private quaternion = new THREE.Quaternion();
  private hasReading = false;
  private compassHeadingDeg: number | null = null;
  private usingAbsolute = false;
  private absoluteFired = false;
  private startedAt = 0;
  private headingSin = 0;
  private headingCos = 0;
  private headingInit = false;
  private handler: ((e: DeviceOrientationEvent) => void) | null = null;

  /**
   * iOS 13+ では体験開始ボタンのタップハンドラ内から呼ぶこと。
   * 権限が拒否された場合は false を返す。
   */
  async requestPermission(): Promise<boolean> {
    const ctor = DeviceOrientationEvent as unknown as DeviceOrientationEventStatic | undefined;
    if (ctor && typeof ctor.requestPermission === 'function') {
      try {
        return (await ctor.requestPermission()) === 'granted';
      } catch {
        return false;
      }
    }
    return true; // 権限APIがない環境はそのまま利用可
  }

  start(): void {
    if (this.handler) return;
    this.startedAt = performance.now();
    this.handler = (e: DeviceOrientationEvent) => this.onEvent(e as DeviceOrientationEventiOS);
    // Androidは absolute イベントを優先。発火しない環境では通常イベントにフォールバック。
    if ('ondeviceorientationabsolute' in window) {
      window.addEventListener('deviceorientationabsolute', this.handler as EventListener);
      this.usingAbsolute = true;
    }
    window.addEventListener('deviceorientation', this.handler as EventListener);
  }

  stop(): void {
    if (!this.handler) return;
    window.removeEventListener('deviceorientationabsolute', this.handler as EventListener);
    window.removeEventListener('deviceorientation', this.handler as EventListener);
    this.handler = null;
  }

  private onEvent(e: DeviceOrientationEventiOS): void {
    if (e.type === 'deviceorientationabsolute') this.absoluteFired = true;
    // absoluteリスナー登録済みなら非absoluteイベント(相対値)は原則無視するが、
    // absoluteが一度も発火しない端末(磁気センサー無効等)では3秒後に相対値へ
    // フォールバックする(姿勢が永遠に取れず何も表示されない状態を避ける。
    // 方位のゼロ点は不定になるため手動キャリブレーションで補正してもらう)
    if (this.usingAbsolute && e.type === 'deviceorientation' && e.webkitCompassHeading === undefined) {
      if (this.absoluteFired || performance.now() - this.startedAt < 3000) return;
    }
    if (e.alpha === null || e.beta === null || e.gamma === null) return;

    let alphaDeg = e.alpha;
    if (e.webkitCompassHeading !== undefined && !Number.isNaN(e.webkitCompassHeading)) {
      // iOS: alphaは起動時基準の相対値なのでコンパス方位(真北基準)から絶対alphaを再構成
      this.compassHeadingDeg = e.webkitCompassHeading;
      alphaDeg = normalizeDeg(360 - e.webkitCompassHeading);
    } else {
      // Android absolute: alphaは磁北基準。heading = 360 - alpha
      this.compassHeadingDeg = normalizeDeg(360 - e.alpha);
    }
    // 方位の円環EMA(XRアライメント等、瞬間値では乱れに弱い用途向け)
    {
      const r = this.compassHeadingDeg * DEG2RAD;
      if (!this.headingInit) {
        this.headingSin = Math.sin(r);
        this.headingCos = Math.cos(r);
        this.headingInit = true;
      } else {
        const a = 0.12;
        this.headingSin += a * (Math.sin(r) - this.headingSin);
        this.headingCos += a * (Math.cos(r) - this.headingCos);
      }
    }

    const screenAngle = (screen.orientation?.angle ?? 0) * DEG2RAD;
    quaternionFromDeviceOrientation(
      this.quaternion,
      alphaDeg * DEG2RAD,
      e.beta * DEG2RAD,
      e.gamma * DEG2RAD,
      screenAngle,
    );
    this.hasReading = true;
  }

  get ready(): boolean {
    return this.hasReading;
  }

  /** デバイスの姿勢(ワールド基準、-Z=北)。yawオフセット適用前の生値 */
  getQuaternion(out: THREE.Quaternion): boolean {
    if (!this.hasReading) return false;
    out.copy(this.quaternion);
    return true;
  }

  /** 磁北基準のコンパス方位[deg](0=北, 時計回り)。未取得ならnull */
  get magneticHeadingDeg(): number | null {
    return this.compassHeadingDeg;
  }

  /** 円環EMAで平滑化した磁北方位[deg]。瞬間的なセンサー乱れの影響を受けにくい */
  get smoothedMagneticHeadingDeg(): number | null {
    if (!this.headingInit) return null;
    return normalizeDeg((Math.atan2(this.headingSin, this.headingCos) * 180) / Math.PI);
  }

  get isAbsolute(): boolean {
    return this.usingAbsolute || this.compassHeadingDeg !== null;
  }
}
